import qs from 'qs';

import { parseJwt } from './user.util';
import { extractWidgetPaymentReturnParams, isKlarnaPaymentCancelReturn } from '../widget/utils/widget-callback.util';

const WIDGET_HASH_STORAGE_KEY = 'widgetEmbedHash';
const WIDGET_RENTAL_STORAGE_KEY = 'widgetRentalUnitType';
const WIDGET_CID_STORAGE_KEY = 'widgetCid';
const WIDGET_SIG_STORAGE_KEY = 'widgetSig';
export const WIDGET_MANAGE_ROUTE_PATH = '/widget/booking/manage';
export const WIDGET_CANCEL_ROUTE_PATH = '/widget/booking/cancel';
export const WIDGET_PAY_ROUTE_PATH = '/widget/booking/pay';
export const WIDGET_CONFIRMED_ROUTE_PATH = '/widget/booking/confirmed';
export const WIDGET_PAYMENT_CANCELLED_ROUTE_PATH = '/paymentcancelled';
const LEGACY_WIDGET_CANCEL_ROUTE_PATH = '/booking';

/**
 * Parse Klarna return query params (TeeSheet order id + Kustom/Klarna order id).
 */
export const getKlarnaConfirmationParamsFromSearch = search => {
  const params = new URLSearchParams(search || '');
  const orderIdRaw = params.get('orderId');
  const kustomRaw = params.get('kustomOrderId');
  const kustomOrderId = typeof kustomRaw === 'string' ? kustomRaw.trim() : '';
  const orderId =
    orderIdRaw !== null && orderIdRaw !== '' && !Number.isNaN(Number(orderIdRaw)) ? Number(orderIdRaw) : NaN;
  const isValid = kustomOrderId !== '' && !Number.isNaN(orderId) && orderId > 0;
  return {
    orderId: isValid ? orderId : null,
    kustomOrderId: isValid ? kustomOrderId : '',
    isValid
  };
};

/**
 * Widget traffic from URL only (no Redux login slice).
 * Matches App.js computeIsWidgetRequest when login.type is unknown.
 *
 * Embed contract (new widget on `/`): `?widget=1` and/or JWT in `hash` with
 * `client_id` === `widgetClient`; optional `rental-unit-type`, `del` (skip bootstrap fetch in full app).
 */
export const isWidgetRequestFromUrl = () => {
  const params = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const { hash, cid, sig } = params;
  let hashData;
  if (hash) {
    try {
      hashData = parseJwt(hash);
    } catch {
      hashData = null;
    }
  }
  return (
    window.location.search.includes('widget') ||
    Boolean(cid && sig) ||
    (hashData && hashData['client_id'] === 'widgetClient')
  );
};

/**
 * New booking widget: path `/` (or trailing slash only) and widget JWT/query rules.
 * `/accommodations` + widget stays on full app (old widget + Redux).
 */
export const isPaymentCancelledRouteRequest = ({ pathname, search }) => {
  const normalizedPath = (pathname || '').replace(/\/$/, '') || '/';
  if (normalizedPath !== WIDGET_PAYMENT_CANCELLED_ROUTE_PATH) {
    return false;
  }
  return isKlarnaPaymentCancelReturn(extractWidgetPaymentReturnParams(search || ''));
};

const isLegacyWidgetDevEnabled = () =>
  process.env.REACT_APP_ENABLE_LEGACY_WIDGET === 'true' || process.env.NODE_ENV === 'development';

/**
 * Local dev: `?legacyWidget=1` forces FullApp → WidgetApp old calendar on `/`.
 * Ignored in production builds unless REACT_APP_ENABLE_LEGACY_WIDGET=true.
 */
export const isLegacyWidgetDevRequest = () => {
  if (!isLegacyWidgetDevEnabled()) {
    return false;
  }
  const params = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  return Boolean(params.legacyWidget);
};

export const isNewWidgetOnly = () => {
  if (isLegacyWidgetDevRequest()) {
    return false;
  }
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  if (path === WIDGET_CONFIRMED_ROUTE_PATH) {
    return getKlarnaConfirmationParamsFromSearch(window.location.search).isValid;
  }
  if (path === WIDGET_PAYMENT_CANCELLED_ROUTE_PATH) {
    return (
      isKlarnaPaymentCancelReturn(extractWidgetPaymentReturnParams(window.location.search)) || isWidgetRequestFromUrl()
    );
  }
  if (
    path !== '/' &&
    path !== WIDGET_MANAGE_ROUTE_PATH &&
    path !== WIDGET_CANCEL_ROUTE_PATH &&
    path !== WIDGET_PAY_ROUTE_PATH
  ) {
    return false;
  }
  return isWidgetRequestFromUrl();
};

/**
 * Redirect old cancel self-service path to the manage booking page.
 * Preserves the existing query string.
 */
export const redirectWidgetCancelToManageIfNeeded = () => {
  const normalizedPath = window.location.pathname.replace(/\/$/, '') || '/';
  if (normalizedPath !== WIDGET_CANCEL_ROUTE_PATH) {
    return false;
  }

  window.location.replace(`${WIDGET_MANAGE_ROUTE_PATH}${window.location.search || ''}`);
  return true;
};

/**
 * Redirect old customer cancellation links to the canonical manage widget route.
 * Keeps existing query params and enriches auth params from session when missing.
 */
export const redirectLegacyWidgetCancelUrlIfNeeded = () => {
  const normalizedPath = window.location.pathname.replace(/\/$/, '') || '/';
  if (normalizedPath !== LEGACY_WIDGET_CANCEL_ROUTE_PATH) {
    return false;
  }

  const params = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const hasLegacySignature = Boolean(params.widget && params.id && params.gcid && params.email && params.odid);
  if (!hasLegacySignature) {
    return false;
  }

  const nextParams = { ...params };

  if (!nextParams.cid && sessionStorage.getItem(WIDGET_CID_STORAGE_KEY)) {
    nextParams.cid = sessionStorage.getItem(WIDGET_CID_STORAGE_KEY);
  }
  if (!nextParams.sig && sessionStorage.getItem(WIDGET_SIG_STORAGE_KEY)) {
    nextParams.sig = sessionStorage.getItem(WIDGET_SIG_STORAGE_KEY);
  }
  if (!nextParams.hash && sessionStorage.getItem(WIDGET_HASH_STORAGE_KEY)) {
    nextParams.hash = sessionStorage.getItem(WIDGET_HASH_STORAGE_KEY);
  }
  if (nextParams.cid || nextParams.sig || nextParams.hash) {
    delete nextParams.widget;
  }
  delete nextParams.del;

  const nextSearch = qs.stringify(nextParams, { addQueryPrefix: true, encode: true });
  window.location.replace(`${WIDGET_MANAGE_ROUTE_PATH}${nextSearch}`);
  return true;
};

const hasValidAuthToken = () =>
  Boolean(
    sessionStorage.authToken &&
      sessionStorage.authToken !== 'undefined' &&
      !sessionStorage.authToken.includes('undefined')
  );

const isWidgetQueryFromParams = params => {
  if (params.widget) {
    return true;
  }
  if (params.cid && params.sig) {
    return true;
  }
  const { hash } = params;
  if (!hash) {
    return false;
  }
  try {
    const hashData = parseJwt(hash);
    return hashData?.['client_id'] === 'widgetClient';
  } catch {
    return false;
  }
};

export const getWidgetAuthParamsFromUrl = () => {
  const params = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const cid = typeof params.cid === 'string' ? params.cid.trim() : '';
  const sig = typeof params.sig === 'string' ? params.sig.trim() : '';
  const hash = typeof params.hash === 'string' ? params.hash.trim() : '';
  return {
    cid: cid || null,
    sig: sig || null,
    hash: hash || null
  };
};

/**
 * Mirror App.js validateIfWidgetRequest session writes without Redux.
 * Call before rendering the lean widget tree.
 *
 * @returns {{ isAuthenticated: boolean, isWidget: boolean, skipBootstrapFetch: boolean }}
 */
export const syncNewWidgetSessionFromUrl = () => {
  const params = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const isWidget = isWidgetQueryFromParams(params);

  if (!isWidget) {
    return { isAuthenticated: false, isWidget: false, skipBootstrapFetch: false };
  }

  const { hash, cid, sig } = params;
  const rentalUnitType = params['rental-unit-type'];

  if (cid && sig) {
    sessionStorage.setItem(WIDGET_CID_STORAGE_KEY, cid);
    sessionStorage.setItem(WIDGET_SIG_STORAGE_KEY, sig);
  }

  if (
    hash &&
    (!sessionStorage.authToken ||
      sessionStorage.authToken === 'undefined' ||
      hash !== sessionStorage.getItem(WIDGET_HASH_STORAGE_KEY) ||
      String(rentalUnitType ?? '') !== String(sessionStorage.getItem(WIDGET_RENTAL_STORAGE_KEY) ?? ''))
  ) {
    sessionStorage.authToken = `Bearer ${hash}`;
    sessionStorage.setItem(WIDGET_HASH_STORAGE_KEY, hash);
    sessionStorage.setItem(WIDGET_RENTAL_STORAGE_KEY, String(rentalUnitType ?? ''));
  }

  const hasToken = hasValidAuthToken();
  const authenticated = Boolean(
    hasToken &&
      (hash ||
        sessionStorage.getItem(WIDGET_HASH_STORAGE_KEY) ||
        (sessionStorage.getItem(WIDGET_CID_STORAGE_KEY) && sessionStorage.getItem(WIDGET_SIG_STORAGE_KEY)))
  );

  return {
    isAuthenticated: authenticated,
    isWidget: true,
    skipBootstrapFetch: !!params.del,
    cid: cid || sessionStorage.getItem(WIDGET_CID_STORAGE_KEY),
    sig: sig || sessionStorage.getItem(WIDGET_SIG_STORAGE_KEY),
    hash: hash || sessionStorage.getItem(WIDGET_HASH_STORAGE_KEY)
  };
};
